import { useEffect, useState } from "react";
import { adminApi, clearAdminToken, getAdminToken } from "../lib/api";
import { AdminLogin } from "./AdminLogin";
import { AdminPanel } from "./AdminPanel";
import { useAnalyticsRole } from "../lib/useAnalyticsRole";

type Phase = "checking" | "login" | "authed";

/**
 * Entry point for /admin. Renders the password screen unless a stored session
 * still validates. No part of the rest of the app links here — you reach it by
 * typing the URL.
 */
export function AdminApp() {
  // Admin pageviews are not sent at all (useRouteAnalytics); whatever else an admin tab
  // emits is labelled so it can be excluded from the product numbers.
  useAnalyticsRole("admin");
  const [phase, setPhase] = useState<Phase>(() => (getAdminToken() ? "checking" : "login"));

  // Validate a stored token on load; a bad/expired one falls back to the login.
  useEffect(() => {
    if (phase !== "checking") return;
    let cancelled = false;
    adminApi
      .session()
      .then(() => !cancelled && setPhase("authed"))
      .catch(() => {
        if (cancelled) return;
        clearAdminToken();
        setPhase("login");
      });
    return () => {
      cancelled = true;
    };
  }, [phase]);

  if (phase === "checking") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--color-ink)]">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-[var(--color-edge)] border-t-[var(--color-accent)]" />
      </div>
    );
  }

  if (phase === "login") {
    return <AdminLogin onSuccess={() => setPhase("authed")} />;
  }

  return <AdminPanel onLogout={() => setPhase("login")} />;
}
